import { UNITS, type LengthUnit } from '@flobi/artwork-core';
import { useCallback, useEffect, useState } from 'react';

/**
 * What a NEW file starts with, as this person likes it: the units its rulers and sizes are said in,
 * whether it is made for screens (RGB) or print (CMYK), and the resolution a PNG comes out at.
 *
 * They are the same three settings Document settings changes per file; this is only where the next
 * file begins. Kept in this browser, like the view's switches, and written into the file once, when
 * the Home opens the file it has just made (`startWith` in the editor's route state).
 */
export interface NewFileDefaults {
  readonly units: LengthUnit;
  readonly colorMode: 'rgb' | 'cmyk';
  readonly resolution: number;
}

export const NEW_FILE_FACTORY: NewFileDefaults = { units: 'px', colorMode: 'rgb', resolution: 72 };
export const RESOLUTIONS = [72, 150, 300, 600] as const;

const KEY = 'artwork:new-file';
const EVENT = 'artwork:new-file-changed';

export function readNewFileDefaults(): NewFileDefaults {
  try {
    const saved = JSON.parse(localStorage.getItem(KEY) ?? 'null') as Partial<Record<keyof NewFileDefaults, unknown>> | null;
    if (!saved) return NEW_FILE_FACTORY;
    return {
      units: (UNITS as readonly string[]).includes(saved.units as string) ? (saved.units as LengthUnit) : NEW_FILE_FACTORY.units,
      colorMode: saved.colorMode === 'cmyk' ? 'cmyk' : 'rgb',
      resolution: typeof saved.resolution === 'number' && RESOLUTIONS.includes(saved.resolution as 72) ? saved.resolution : NEW_FILE_FACTORY.resolution,
    };
  } catch {
    return NEW_FILE_FACTORY;
  }
}

/** Whether a file would start any differently from how every file starts. */
export const differsFromFactory = (one: NewFileDefaults): boolean =>
  one.units !== NEW_FILE_FACTORY.units || one.colorMode !== NEW_FILE_FACTORY.colorMode || one.resolution !== NEW_FILE_FACTORY.resolution;

/** The defaults, and a way to change one of them. */
export function useNewFileDefaults(): [NewFileDefaults, (patch: Partial<NewFileDefaults>) => void] {
  const [value, setValue] = useState<NewFileDefaults>(readNewFileDefaults);
  useEffect(() => {
    const follow = () => setValue(readNewFileDefaults());
    window.addEventListener(EVENT, follow);
    window.addEventListener('storage', follow);
    return () => {
      window.removeEventListener(EVENT, follow);
      window.removeEventListener('storage', follow);
    };
  }, []);
  const change = useCallback((patch: Partial<NewFileDefaults>) => {
    const next = { ...readNewFileDefaults(), ...patch };
    try {
      localStorage.setItem(KEY, JSON.stringify(next));
    } catch {
      // A private window can refuse storage: the choice holds for this visit.
    }
    setValue(next);
    window.dispatchEvent(new Event(EVENT));
  }, []);
  return [value, change];
}
